import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  startOfMonth,
  endOfMonth,
  addDays,
  format,
  isSameDay,
  addMonths,
  startOfWeek,
  endOfWeek,
  addWeeks,
  differenceInCalendarDays,
  sub,
} from "date-fns";
import { FaChevronLeft, FaChevronRight, FaHome, FaUsers } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import axios from "axios";
import API_BASE_URL from "../config";

const AdminTimesheetGrid = () => {
  const navigate = useNavigate();
  const invalidDateToastShown = useRef(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [worklogs, setWorklogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [viewMode, setViewMode] = useState("monthly");
  const [billableFilter, setBillableFilter] = useState("all");
  const [pageLoading, setPageLoading] = useState(true);   // initial page load
  const [dataLoading, setDataLoading] = useState(false); // employee switch

  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
  const [error, setError] = useState("");
  const [holidayDates, setHolidayDates] = useState([]);
  const [holidayMap, setHolidayMap] = useState({});
  const [weeklyHolidays, setWeeklyHolidays] = useState([]);
  const [leaveDates, setLeaveDates] = useState([]);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [financialYear, setFinancialYear] = useState("");

  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [manager, setManager] = useState(null);
  const [evaluationData, setEvaluationData] = useState({
    managerName: "",
    employeeName: "",
    employeeType: "",
    month: "",
    week: "",
    assignedTasks: 0,
    completedTasks: 0,
    billingHours: 0,
    ratings: {
      taskCompletion: 0,
      quality: 0,
      teamwork: 0,
      initiative: 0,
      learning: 0
    },
    remarks: ""
  });

  const token = localStorage.getItem("token");

  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

  const formatHours = (n) =>
    Number.isFinite(n) ? round2(n).toFixed(2) : "0.00";

  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");

    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
      return;
    }

    //  check admin role
    if (!roles.includes("ROLE_ADMIN")) {
      toast.error("You are not authorized to access this page.");
      navigate("/");
      return;
    }
  }, [navigate]);


  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(`${API_BASE_URL}/api/company-info`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then(res => res.json())
      .then(data => {


        // API returns array → take first record
        const company = Array.isArray(data) ? data[0] : data;

        if (!company?.financialYear) {
          console.warn("No financialYear found");
          return;
        }

        setFinancialYear(company.financialYear);
      })

      .catch(err => console.error("Error fetching company info:", err));
  }, []);

  // Load employees for admin
  useEffect(() => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("token");

    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/", { replace: true });
      return;
    }

    const fetchEmployees = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/employees/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmployees(res.data);

        // get logged in employee id
        const loggedEmployeeId = localStorage.getItem("employeeId");

        if (loggedEmployeeId) {
          const loggedEmployee = res.data.find(
            (emp) => String(emp.id) === String(loggedEmployeeId)
          );

          if (loggedEmployee) {
            setManager(loggedEmployee); // ✅ STORE MANAGER
            setSelectedEmployee(loggedEmployee);
          } else if (res.data.length > 0) {
            setSelectedEmployee(res.data[0]);
          }
        } else if (res.data.length > 0) {
          setSelectedEmployee(res.data[0]);
        }
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Unable to fetch employees.");
      }
    };

    fetchEmployees();
  }, [navigate]);

  // Load Bootstrap dynamically
  useEffect(() => {
    // Bootstrap CSS
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    bootstrapLink.id = "timesheetgrid-bootstrap";
    document.head.appendChild(bootstrapLink);

    // Bootstrap Icons CSS
    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    iconsLink.id = "timesheetgrid-icons";
    document.head.appendChild(iconsLink);

    return () => {
      const existingBootstrap = document.getElementById("timesheetgrid-bootstrap");
      if (existingBootstrap) existingBootstrap.remove();

      const existingIcons = document.getElementById("timesheetgrid-icons");
      if (existingIcons) existingIcons.remove();
    };
  }, []);


  useEffect(() => {
    fetchWeeklyHolidays();
  }, []);

  async function fetchWeeklyHolidays() {
    try {
      const res = await axios.get("/api/holidays/weekly", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const days = res.data.map((h) => h.name);
      setWeeklyHolidays(days);
    } catch (err) {
      console.error("Failed to fetch weekly holidays:", err);
    }
  }


  const isWeeklyHoliday = (day) => {
    const dayName = format(day, "EEEE"); // e.g. "Sunday"
    return weeklyHolidays.includes(dayName);
  };


  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/holidays`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error("Failed to fetch holidays");
        }

        const data = await res.json();

        const allHolidayDates = [];
        const holidayNameMap = {};

        data.forEach((h) => {
          const start = new Date(h.startDate);
          const end = new Date(h.endDate);

          // Go through each date between start and end
          for (
            let d = new Date(start);
            d <= end;
            d.setDate(d.getDate() + 1)
          ) {
            const formatted = d.toISOString().split("T")[0];
            allHolidayDates.push(formatted);
            holidayNameMap[formatted] = h.name;
          }
        });

        setHolidayDates(allHolidayDates);
        setHolidayMap(holidayNameMap);
        console.log("Fetched holidays:", allHolidayDates);
      } catch (err) {
        console.error("Error fetching holidays:", err);
      }
    };

    fetchHolidays();
  }, [token]);


  // Load worklogs and tasks when employee changes
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!selectedEmployee || !token) return;

    const fetchWorklogsAndTasks = async () => {
      setDataLoading(true);

      try {
        //  Fetch ALL worklogs
        const logsRes = await axios.get(
          `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const allWorklogs = Array.isArray(logsRes.data) ? logsRes.data : [];

        //  Filter worklogs by current startDate & endDate (FRONTEND)
        const filteredLogs = allWorklogs.filter((log) => {
          if (!log.date) return false;

          const logDate = new Date(log.date);
          return logDate >= startDate && logDate <= endDate;
        });

        setWorklogs(filteredLogs);

        //  Extract task IDs from filtered worklogs
        const taskIdSet = new Set(
          filteredLogs.map((log) => log.taskId).filter(Boolean)
        );

        //  Fetch ALL tasks
        const tasksRes = await axios.get(
          `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}/tasks`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const allTasks = Array.isArray(tasksRes.data) ? tasksRes.data : [];

        //  Keep ONLY tasks used in current month
        const filteredTasks = allTasks.filter((task) =>
          taskIdSet.has(task.id)
        );

        setTasks(filteredTasks);
      } catch (err) {
        console.error("Error fetching worklogs:", err);
        setError("Unable to fetch timesheet data.");
      } finally {
        setDataLoading(false);
      }
    };

    fetchWorklogsAndTasks();
  }, [selectedEmployee, startDate, endDate]);



  const handleSubmitEvaluation = async () => {
    try {
      // ✅ safety check
      if (!selectedEmployee?.id || !manager?.id) {
        toast.error("Employee or Manager missing");
        return;
      }

      // ✅ build payload
      const payload = {
        employeeId: selectedEmployee.id,
        managerId: manager.id,

        employeeType: evaluationData.employeeType,
        month: evaluationData.month,
        week: evaluationData.week,

        assignedTasks: Number(evaluationData.assignedTasks) || 0,
        completedTasks: Number(evaluationData.completedTasks) || 0,
        billingHours: Number(evaluationData.billingHours) || 0,

        taskCompletion: Number(evaluationData.ratings.taskCompletion) || 0,
        quality: Number(evaluationData.ratings.quality) || 0,
        teamwork: Number(evaluationData.ratings.teamwork) || 0,
        initiative: Number(evaluationData.ratings.initiative) || 0,
        learning: Number(evaluationData.ratings.learning) || 0,

        remarks: evaluationData.remarks || ""
      };

      console.log("Submitting payload:", payload); // DEBUG

      await axios.post(`${API_BASE_URL}/api/evaluations`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Evaluation submitted successfully!");
      setShowEvaluationModal(false);

      // 👉 navigate after submit
      navigate("/evaluation"); // change route if needed

    } catch (err) {
      console.error(err);
      toast.error("Failed to submit evaluation");
    }
  };




  const fetchLeaves = async () => {
    if (!selectedEmployee || !selectedEmployee.id || !financialYear) {
      console.warn("Employee or financial year missing — skipping fetchLeaves");
      setLeaveDates([]);
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/leave-transactions/employee/${selectedEmployee.id}/avail`,
        {
          params: { financialYear },   //  important
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const leaveList = [];

      //  Only APPROVED AV leaves
      res.data
        .filter(lv => lv.leaveStatus === "APPROVED") //  backend field
        .forEach(lv => {
          const start = new Date(lv.startDate);
          const end = new Date(lv.endDate);

          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            leaveList.push(d.toISOString().split("T")[0]);
          }
        });

      setLeaveDates(leaveList);
    } catch (err) {
      console.error("Error fetching leave:", err);
      setLeaveDates([]);
    }
  };


  useEffect(() => {
    if (selectedEmployee && financialYear) {
      fetchLeaves();
    }
  }, [selectedEmployee, financialYear]);



  const isLeaveDay = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return leaveDates.includes(formatted);
  };



  const isHolidayColumn = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    return holidayDates.includes(formatted);
  };


  const handleExport = async (fileFormat) => {
    if (!selectedEmployee) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      // Format dates as yyyy-MM-dd
      const fromDateStr = startDate ? startDate.toISOString().split("T")[0] : "";
      const toDateStr = endDate ? endDate.toISOString().split("T")[0] : "";

      // Build URL params
      const params = new URLSearchParams();
      params.append("type", "date"); // must be "date" for fromDate/toDate filter
      if (fromDateStr) params.append("fromDate", fromDateStr);
      if (toDateStr) params.append("toDate", toDateStr);

      // Add billing type separately as a query param if needed
      if (billableFilter && billableFilter !== "all") params.append("billingType", billableFilter);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/timesheet/${selectedEmployee.id}?${params.toString()}&format=${fileFormat}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          fileFormat === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Timesheet_${selectedEmployee.firstName}_${selectedEmployee.lastName}.${fileFormat === "pdf" ? "pdf" : "xlsx"
        }`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export report. Please try again.");
    }
  };






  // Generate dynamic days
  const dayCount = differenceInCalendarDays(endDate, startDate) + 1;
  const days = Array.from({ length: dayCount }, (_, i) => addDays(startDate, i));

  const handlePrev = () => {
    if (viewMode === "monthly") {
      const newStart = sub(startDate, { months: 1 });
      setStartDate(newStart);
      setEndDate(endOfMonth(newStart));
    } else {
      const newStart = sub(startDate, { weeks: 1 });
      setStartDate(newStart);
      setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
    }
  };

  const handleNext = () => {
    if (viewMode === "monthly") {
      const newStart = addMonths(startDate, 1);
      setStartDate(newStart);
      setEndDate(endOfMonth(newStart));
    } else {
      const newStart = addWeeks(startDate, 1);
      setStartDate(newStart);
      setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
    }
  };



  const filteredWorklogs = (() => {
    if (!startDate && !endDate) return worklogs;

    const from = startDate ? new Date(startDate) : null;
    const to = endDate ? new Date(endDate) : null;

    if (from && to && to < from) {
      if (!invalidDateToastShown.current) {
        toast.error("End date must be greater than or equal to Start date.");
        invalidDateToastShown.current = true;
      }
      return worklogs;
    } else {
      invalidDateToastShown.current = false;
    }

    return worklogs.filter((log) => {
      const logDate = new Date(log.date);

      const inStart = from ? logDate >= from : true;
      const inEnd = to ? logDate <= to : true;

      if (!(inStart && inEnd)) return false;

      if (billableFilter === "all") return true;

      return log.billingType === billableFilter;
    });
  })();




  const getHours = (taskId, day, billableType) =>
    round2(
      filteredWorklogs
        .filter(
          (log) =>
            log.taskId === taskId &&
            isSameDay(new Date(log.date), day) &&
            (!billableType || log.billingType === billableType)
        )
        .reduce((sum, log) => sum + log.hours, 0)
    );

  const totalHours = filteredWorklogs.reduce((sum, log) => sum + log.hours, 0);
  const totalBillable = filteredWorklogs
    .filter((log) => log.billingType === "billable")
    .reduce((sum, log) => sum + log.hours, 0);
  const totalNonBillable = totalHours - totalBillable;

  const totalFilteredHours = round2(
    filteredWorklogs.reduce((sum, log) => sum + log.hours, 0)
  );

  const totalFilteredBillable = round2(
    filteredWorklogs
      .filter((log) => log.billingType === "billable")
      .reduce((sum, log) => sum + log.hours, 0)
  );

  const totalFilteredNonBillable = round2(
    filteredWorklogs
      .filter((log) => log.billingType === "non-billable")
      .reduce((sum, log) => sum + log.hours, 0)
  );

  const HOURS_PER_DAY = 8;

  // total days of month
  const monthDays = days.length;

  // working days
  const workingDays = days.filter((day) => {
    const formatted = format(day, "yyyy-MM-dd");

    return (
      !isWeeklyHoliday(day) &&
      !holidayDates.includes(formatted) &&
      !leaveDates.includes(formatted)
    );
  }).length;

  const totalMonthHours = workingDays * HOURS_PER_DAY;

  // logged hours calculated already
  const loggedMonthHours = totalFilteredHours;


  const getFooterBg = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    const isHoliday = holidayDates.includes(formatted);
    const isWeekly = isWeeklyHoliday(day);
    const isLeave = leaveDates.includes(formatted);

    if (isLeave) return "#fff0f0";       // Leave
    if (isHoliday) return "#d4f8d4";     // Holiday
    if (isWeekly) return "#f7f7f7";      // Weekly off
    return "#f8f9fa";                    // Default footer bg-light
  };


  const calculateEvaluationData = () => {
    if (!selectedEmployee) return;

    const managerName = manager
      ? `${manager.firstName} ${manager.lastName}`
      : "Manager";

    const employeeName = `${selectedEmployee.firstName} ${selectedEmployee.lastName}`;

    // Month & Week
    const month = format(startDate, "MMMM yyyy");
    const week =
      viewMode === "weekly"
        ? `${format(startDate, "dd MMM")} - ${format(endDate, "dd MMM")}`
        : "";

    // Assigned tasks
    const assignedTasks = tasks.length;

    // Completed tasks
    const completedTasks = tasks.filter(task =>
      task.status === "COMPLETED"
    ).length;

    // Billing hours
    const billingHours = totalFilteredBillable;

    // ✅ IMPORTANT: Ratings should be EMPTY (manual input)
    const ratings = {
      taskCompletion: 0,
      quality: 0,
      teamwork: 0,
      initiative: 0,
      learning: 0
    };

    return {
      managerName,
      employeeName,
      employeeType: selectedEmployee.employeeType || "Full-Time",
      month,
      week,
      assignedTasks,
      completedTasks,
      billingHours,

      // 👇 manual ratings
      ratings,

      // 👇 will be calculated dynamically
      finalScore: 0,
      grade: "-",

      remarks: ""
    };
  };


  const calculateOverallPerformance = () => {
    const values = Object.values(evaluationData.ratings)
      .map(Number)
      .filter(v => v > 0);

    if (!values.length) return { score: 0, grade: "-" };

    const avg =
      values.reduce((a, b) => a + b, 0) / values.length;

    let grade = "C";

    if (avg >= 4.5) grade = "A+";
    else if (avg >= 4) grade = "A";
    else if (avg >= 3.5) grade = "B+";
    else if (avg >= 3) grade = "B";
    else if (avg >= 2) grade = "C";
    else grade = "D";

    return {
      score: avg.toFixed(2),
      grade
    };
  };

  useEffect(() => {
    const { score, grade } = calculateOverallPerformance();

    setEvaluationData(prev => ({
      ...prev,
      finalScore: score,
      grade: grade
    }));
  }, [evaluationData.ratings]);

  {
    pageLoading && (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <div className="spinner-border text-primary"></div>
        <span className="ms-2 fs-5">Loading timesheet...</span>
      </div>
    )
  }


  return (

    <>
      <style>
        {`
  .emp-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    background: #e9ecef;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: #555;
    flex-shrink: 0;
  }

  .emp-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .emp-dropdown-item:hover {
    background-color: #f8f9fa;
  }
`}
      </style>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top">
        <AdminNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "110px" }}></div>
      <div className="container-fluid mt-4">
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-white rounded shadow-sm border gap-3">
          {/* Employee Selector */}
          <div className="position-relative">
            {/* Selected Employee */}
            <div
              className="d-flex align-items-center gap-2 border rounded px-2 py-1 bg-white"
              style={{ minWidth: "260px", cursor: "pointer" }}
              onClick={() => setShowEmployeeDropdown((v) => !v)}
            >
              <div className="emp-avatar">
                {selectedEmployee?.photoPath ? (
                  <img
                    src={`${API_BASE_URL}/uploads/${selectedEmployee.photoPath}`}
                    onError={(e) => (e.currentTarget.src = "/default-avatar.png")}
                    alt="emp"
                  />
                ) : (
                  selectedEmployee?.firstName?.charAt(0)?.toUpperCase()
                )}
              </div>

              <div className="flex-grow-1 small fw-semibold">
                {selectedEmployee
                  ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
                  : "Select Employee"}
              </div>

              <i className="bi bi-chevron-down"></i>
            </div>

            {/* Dropdown */}
            {showEmployeeDropdown && (
              <div
                className="position-absolute bg-white border rounded shadow-sm mt-1"
                style={{
                  width: "100%",
                  maxHeight: "260px",
                  overflowY: "auto",
                  zIndex: 1000,
                }}
              >
                {employees.map((emp) => (
                  <div
                    key={emp.id}
                    className="d-flex align-items-center gap-2 px-2 py-2 emp-dropdown-item"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedEmployee(emp);
                      setShowEmployeeDropdown(false);
                    }}
                  >
                    <div className="emp-avatar">
                      {emp.photoPath ? (
                        <img
                          src={`${API_BASE_URL}/uploads/${emp.photoPath}`}
                          onError={(e) => (e.currentTarget.src = "/default-avatar.png")}
                          alt="emp"
                        />
                      ) : (
                        emp.firstName?.charAt(0)?.toUpperCase()
                      )}
                    </div>

                    <div className="small">
                      <div className="fw-semibold">
                        {emp.firstName} {emp.lastName}
                      </div>
                      {/* <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        ID: {emp.employeeId}
                      </div> */}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>


          {/* Controls */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
              onClick={() => navigate("/admindashboard")}
            >
              <FaHome /> Home
            </button>
            <button
              className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
              onClick={() => navigate(`/alltimesheetgrid`)}
            >
              <FaUsers /> All Employee
            </button>
            <button
              className={`btn btn-sm ${viewMode === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setViewMode("monthly");
                setStartDate(startOfMonth(startDate));
                setEndDate(endOfMonth(startDate));
              }}
            >
              Monthly
            </button>
            <button
              className={`btn btn-sm ${viewMode === "weekly" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                const today = new Date(); // Get today's date
                setViewMode("weekly");
                setStartDate(startOfWeek(today, { weekStartsOn: 1 })); // Monday as start of week
                setEndDate(endOfWeek(today, { weekStartsOn: 1 }));     // Sunday as end of week
              }}
            >
              Weekly
            </button>

            <div className="btn-group btn-group-sm">
              <button
                className={`btn btn-sm ${billableFilter === "all" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("all")}
              >
                All
              </button>
              <button
                className={`btn btn-sm ${billableFilter === "billable" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("billable")}
              >
                Billable
              </button>
              <button
                className={`btn btn-sm ${billableFilter === "non-billable" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => setBillableFilter("non-billable")}
              >
                Non-Billable
              </button>
            </div>

            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-primary" onClick={handlePrev}>
                <FaChevronLeft />
              </button>
              <button className="btn btn-outline-primary" onClick={handleNext}>
                <FaChevronRight />
              </button>
            </div>
            {/* date range */}
            <div className="d-flex gap-1 align-items-center">
              <input
                type="date"
                value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
                onChange={(e) =>
                  setStartDate(e.target.value ? new Date(e.target.value) : null)
                }
                className="form-control form-control-sm"
                style={{ width: "110px" }}
              />

              <span className="text-muted">to</span>

              <input
                type="date"
                value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                onChange={(e) => {
                  const selected = e.target.value ? new Date(e.target.value) : null;

                  if (selected && startDate && selected < startDate) {
                    const today = new Date();
                    setEndDate(today);
                    toast.error(
                      "End date cannot be earlier than Start date. Reset to today."
                    );
                  } else {
                    setEndDate(selected);
                  }
                }}
                className="form-control form-control-sm"
                style={{ width: "110px" }}
              />
            </div>


            {/* Export Buttons */}
            <div className="btn-group btn-group-sm ms-2">

              <button
                className="btn btn-sm btn-warning ms-2"
                onClick={() => {
                  const autoData = calculateEvaluationData();

                  setEvaluationData(autoData);
                  setShowEvaluationModal(true);
                }}
              >
                Evaluation
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={() => handleExport("pdf")}
              >
                Export PDF
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleExport("excel")}
              >
                Export Excel
              </button>
            </div>

          </div>
        </div>

        {/* Summary cards */}
        <div className="row g-2 mb-3 mt-2">
          {[
            { title: "Total Hours", value: totalFilteredHours, bg: "primary", icon: "bi-hourglass-split" },
            { title: "Billable", value: totalFilteredBillable, bg: "success", icon: "bi-cash-coin" },
            { title: "Non-Billable", value: totalFilteredNonBillable, bg: "danger", icon: "bi-slash-circle" },

            // NEW CARD
            {
              title: "Monthly Utilization",
              value: `${formatHours(loggedMonthHours)} / ${totalMonthHours}`,
              bg: "success",
              icon: "bi-calendar-check",
              isRatio: true,
            },
          ].map((card, idx) => (
            <div key={idx} className="col-12 col-sm-4 col-md-2">
              <div className="card shadow-sm border-0 h-100 p-2">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className={`rounded-circle bg-${card.bg} bg-opacity-10 text-${card.bg} d-flex align-items-center justify-content-center`}
                    style={{ width: "35px", height: "35px" }}
                  >
                    <i className={`bi ${card.icon} fs-6`}></i>
                  </div>

                  <div className="flex-grow-1">
                    <h6 className="mb-0 fw-semibold" style={{ fontSize: "0.85rem" }}>
                      {card.title}
                    </h6>

                    {card.isRatio ? (
                      <>
                        <h6 className={`mb-0 text-${card.bg} fw-bold`} style={{ fontSize: "0.9rem" }}>
                          {card.isRatio ? card.value : formatHours(card.value)}
                        </h6>
                      </>
                    ) : (
                      <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                        {formatHours(card.value)} hrs
                      </small>
                    )}
                  </div>





                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Timesheet Grid */}
        <div
          className="shadow-sm rounded border"
          style={{
            maxHeight: "65vh",
            overflow: "auto",
            position: "relative"
          }}
        >
          <table
            className="table table-bordered mb-0 text-center"
            style={{
              tableLayout: "fixed",
              minWidth: 200 + (days.length * 70) + 90
            }}
          >

            {/* ================= HEADER ================= */}
            <thead style={{ position: "sticky", top: 0, zIndex: 50 }}>
              <tr>
                {/* Sticky Task Header */}
                <th
                  className="bg-secondary text-white text-start px-3"
                  style={{
                    minWidth: "200px",
                    width: "200px",
                    left: 0,
                    position: "sticky",
                    zIndex: 60,
                    height: "65px",
                    verticalAlign: "middle"
                  }}
                >
                  Task
                </th>

                {/* Days Header */}
                {days.map((day, i) => {
                  const formatted = format(day, "yyyy-MM-dd");
                  const isHoliday = holidayDates.includes(formatted);
                  const holidayName = holidayMap[formatted];

                  return (
                    <th
                      key={i}
                      style={{
                        minWidth: "70px",
                        backgroundColor: isLeaveDay(day)
                          ? "#ffe6e6"
                          : isHolidayColumn(day)
                            ? "#d4f8d4"
                            : isWeeklyHoliday(day)
                              ? "#f7f7f7"
                              : "#f1f3f5"
                      }}
                      title={
                        isHolidayColumn(day)
                          ? holidayName
                          : isWeeklyHoliday(day)
                            ? "Weekly Holiday"
                            : ""
                      }
                    >
                      {format(day, "d")}
                      <br />
                      <small
                        className={
                          isLeaveDay(day)
                            ? "text-danger fw-semibold"
                            : isHolidayColumn(day) || isWeeklyHoliday(day)
                              ? "text-secondary fw-semibold"
                              : "text-muted"
                        }
                      >
                        {format(day, "EEE")}
                      </small>
                    </th>
                  );
                })}

                {/* Sticky Total Header */}
                <th
                  className="bg-secondary text-white"
                  style={{
                    minWidth: "90px",
                    top: 0,
                    right: 0,
                    position: "sticky",
                    zIndex: 60
                  }}
                >
                  Total
                </th>
              </tr>
            </thead>

            {/* ================= BODY ================= */}
            <tbody>
              {tasks.length ? (
                tasks.map((task, i) => {
                  const rowTotal = round2(
                    days.reduce((sum, day) => sum + getHours(task.id, day), 0)
                  );

                  return (
                    <tr key={task.id || i}>

                      {/* Sticky Task Column */}
                      <td
                        className="text-start px-2"
                        style={{
                          position: "sticky",
                          top: 0,
                          left: 0,
                          minWidth: "200px",
                          width: "200px",
                          background: "white",
                          zIndex: 40
                        }}

                      >
                        <a
                          href={`/tasks?taskId=${task.id}`}
                          style={{
                            display: "block",
                            maxWidth: "180px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textDecoration: "none",
                            color: "#000"
                          }}
                          title={task.title}
                          onClick={(e) => {
                            e.preventDefault();

                            localStorage.setItem("activeTaskId", String(task.id));
                            localStorage.setItem("activeTaskObj", JSON.stringify(task));

                            window.open(`/tasks?taskId=${task.id}`, "_blank");
                          }}

                        >
                          <span style={{ color: "#0d6efd" }}>
                            {task.taskCode}
                          </span>
                          <span>
                            {" - "}{task.title || `Task ${task.id}`}
                          </span>
                        </a>
                      </td>

                      {/* Days Cells */}
                      {days.map((day, j) => {
                        const logs = filteredWorklogs.filter(
                          (log) =>
                            log.taskId === task.id &&
                            isSameDay(new Date(log.date), day)
                        );

                        const totalHours = logs.reduce(
                          (sum, l) => sum + (l.hours || 0),
                          0
                        );

                        return (
                          <td
                            key={j}
                            style={{
                              backgroundColor: isLeaveDay(day)
                                ? "#fff0f0"
                                : isHolidayColumn(day)
                                  ? "#d4f8d4"
                                  : isWeeklyHoliday(day)
                                    ? "#f7f7f7"
                                    : "",
                              //cursor: "pointer"
                            }}
                            title={logs
                              .map(
                                (l) =>
                                  `${l.description || "No description"} (${l.hours}h)`
                              )
                              .join("\n")}
                          //onClick={() => handleCellClick(task, day)}
                          >
                            {totalHours ? (
                              <span className="fw-semibold">
                                {formatHours(totalHours)}
                              </span>
                            ) : (
                              <span style={{ visibility: "hidden" }}>0</span>
                            )}
                          </td>
                        );
                      })}

                      {/* Sticky Total Column */}
                      <td
                        className="fw-semibold"
                        style={{
                          position: "sticky",
                          top: 0,
                          right: 0,
                          background: "white",
                          zIndex: 40
                        }}
                      >
                        {formatHours(rowTotal) || "0"}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={days.length + 2} className="text-muted py-4">
                    No tasks
                  </td>
                </tr>
              )}
            </tbody>

            {/* ================= FOOTER ================= */}
            <tfoot style={{ position: "sticky", bottom: 0, zIndex: 50 }}>

              {/* Billable */}
              <tr>
                <td
                  className="fw-semibold text-start px-3 bg-light"
                  style={{
                    position: "sticky",
                    left: 0,
                    minWidth: "200px",
                    width: "200px",
                    zIndex: 60
                  }}

                >
                  Billable Total
                </td>

                {days.map((day, i) => {
                  const billableTotal = round2(
                    tasks.reduce(
                      (sum, task) =>
                        sum + getHours(task.id, day, "billable"),
                      0
                    )
                  );
                  return (
                    <td key={i} className=" fw-semibold" style={{
                      backgroundColor: getFooterBg(day)
                    }}>
                      {billableTotal
                        ? formatHours(billableTotal)
                        : ""}
                    </td>
                  );
                })}

                <td
                  className="fw-semibold bg-light"
                  style={{ position: "sticky", right: 0, zIndex: 60 }}
                >
                  {formatHours(totalBillable)}
                </td>
              </tr>

              {/* Non-Billable */}
              <tr>
                <td
                  className="fw-semibold text-start px-3 bg-light"
                  style={{ position: "sticky", left: 0, zIndex: 60 }}
                >
                  Non-Billable Total
                </td>

                {days.map((day, i) => {
                  const total = round2(
                    tasks.reduce(
                      (sum, task) =>
                        sum + getHours(task.id, day, "non-billable"),
                      0
                    )
                  );
                  return (
                    <td key={i} className=" fw-semibold" style={{
                      backgroundColor: getFooterBg(day)
                    }}>
                      {total ? formatHours(total) : ""}
                    </td>
                  );
                })}

                <td
                  className="fw-semibold bg-light"
                  style={{ position: "sticky", right: 0, zIndex: 60 }}
                >
                  {formatHours(totalNonBillable)}
                </td>
              </tr>

              {/* Daily Total */}
              <tr>
                <td
                  className="fw-semibold text-start px-3 bg-light"
                  style={{ position: "sticky", left: 0, zIndex: 60 }}
                >
                  Daily Total
                </td>

                {days.map((day, i) => {
                  const total = round2(
                    tasks.reduce(
                      (sum, task) =>
                        sum + getHours(task.id, day),
                      0
                    )
                  );
                  return (
                    <td key={i} className=" fw-semibold" style={{
                      backgroundColor: getFooterBg(day)
                    }}>
                      {total ? formatHours(total) : ""}
                    </td>
                  );
                })}

                <td
                  className="fw-semibold bg-light"
                  style={{ position: "sticky", right: 0, zIndex: 60 }}
                >
                  {formatHours(totalHours)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

      </div>
      {showEvaluationModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{
            background: "rgba(0,0,0,0.4)",
            backdropFilter: "blur(4px)"
          }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">

              {/* HEADER */}
              <div
                className="modal-header text-white rounded-top-4"

              >
                <h5 className="modal-title fw-bold ">Employee Evaluation</h5>
                <button
                  className="btn-close btn-close-black"
                  onClick={() => setShowEvaluationModal(false)}
                ></button>
              </div>

              {/* BODY */}
              <div className="modal-body p-4">

                <div className="row g-4">

                  {/* LEFT SIDE */}
                  <div className="col-lg-6">

                    {/* EMPLOYEE DETAILS */}
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body">
                        <h6 className="fw-bold text-primary mb-3">👤 Employee Details</h6>

                        <div className="row g-3">
                          <div className="col-12">
                            <label className="small text-muted">Manager</label>
                            <input className="form-control bg-light" value={evaluationData.managerName} readOnly />
                          </div>

                          <div className="col-12">
                            <label className="small text-muted">Employee</label>
                            <input className="form-control bg-light" value={evaluationData.employeeName} readOnly />
                          </div>

                          <div className="col-12">
                            <label className="small text-muted">Employee Type</label>
                            <select
                              className="form-select"
                              value={evaluationData.employeeType}
                              onChange={(e) =>
                                setEvaluationData(prev => ({
                                  ...prev,
                                  employeeType: e.target.value
                                }))
                              }
                            >
                              <option value="">Select</option>
                              <option>Full-Time</option>
                              <option>Intern</option>
                              <option>Trainee</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* WORK SUMMARY */}
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <h6 className="fw-bold text-success mb-3">📊 Work Summary</h6>

                        <div className="row g-3">
                          {[
                            { label: "Month", value: evaluationData.month },
                            { label: "Week", value: evaluationData.week },
                            { label: "Assigned Tasks", value: evaluationData.assignedTasks },
                            { label: "Completed Tasks", value: evaluationData.completedTasks },
                            { label: "Billing Hours", value: evaluationData.billingHours }
                          ].map((item, i) => (
                            <div key={i} className="col-6">
                              <label className="small text-muted">{item.label}</label>
                              <input className="form-control bg-light" value={item.value} readOnly />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* RIGHT SIDE */}
                  <div className="col-lg-6">

                    {/* RATINGS */}
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body">
                        <h6 className="fw-bold text-warning mb-3">⭐ Performance Ratings</h6>

                        {[
                          { label: "Task Completion", key: "taskCompletion" },
                          { label: "Quality Of Task", key: "quality" },
                          { label: "Team Work", key: "teamwork" },
                          { label: "Initiative", key: "initiative" },
                          { label: "Learning", key: "learning" }
                        ].map((item, idx) => (
                          <div
                            key={idx}
                            className="d-flex justify-content-between align-items-center mb-3 p-2 rounded"
                            style={{ background: "#f8f9fa" }}
                          >
                            <span className="fw-semibold">{item.label}</span>

                            <div>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <i
                                  key={star}
                                  className={`bi ${star <= evaluationData.ratings[item.key]
                                    ? "bi-star-fill text-warning"
                                    : "bi-star text-secondary"
                                    } fs-4 me-1`}
                                  style={{
                                    cursor: "pointer",
                                    transition: "0.2s",
                                    transform: "scale(1.1)"
                                  }}
                                  onClick={() =>
                                    setEvaluationData(prev => ({
                                      ...prev,
                                      ratings: {
                                        ...prev.ratings,
                                        [item.key]: star
                                      }
                                    }))
                                  }
                                ></i>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* PERFORMANCE */}
                    <div className="card border-0 shadow-sm">
                      <div className="card-body text-center">

                        <h6 className="fw-bold text-info mb-2">📈 Overall Performance</h6>

                        <h1 className="fw-bold text-success">
                          {evaluationData.finalScore}
                          <span className="fs-6 text-muted"> / 5</span>
                        </h1>

                        <div className="progress mt-3 mb-3" style={{ height: "10px", borderRadius: "10px" }}>
                          <div
                            className="progress-bar bg-success"
                            style={{
                              width: `${(evaluationData.finalScore / 5) * 100}%`,
                              borderRadius: "10px"
                            }}
                          ></div>
                        </div>

                        <span className={`badge px-4 py-2 fs-6 ${evaluationData.grade.startsWith("A")
                          ? "bg-success"
                          : evaluationData.grade.startsWith("B")
                            ? "bg-primary"
                            : evaluationData.grade.startsWith("C")
                              ? "bg-warning"
                              : "bg-danger"
                          }`}>
                          Grade: {evaluationData.grade}
                        </span>

                      </div>
                    </div>

                  </div>

                  {/* REMARKS FULL WIDTH */}
                  <div className="col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <h6 className="fw-bold text-danger mb-2">📝 Remarks</h6>
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Write feedback..."
                          value={evaluationData.remarks}
                          onChange={(e) =>
                            setEvaluationData(prev => ({
                              ...prev,
                              remarks: e.target.value
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* FOOTER */}
              <div className="modal-footer border-0">

                <button
                  className="btn btn-success px-4 shadow"
                  onClick={handleSubmitEvaluation}
                >
                  Submit
                </button>

                <button
                  className="btn btn-light px-4"
                  onClick={() => setShowEvaluationModal(false)}
                >
                  Cancel
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default AdminTimesheetGrid;
